import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { FiMic, FiMicOff, FiPlay, FiPause, FiArrowLeft, FiArrowRight, FiEye, FiEyeOff, FiCheck, FiClock } from 'react-icons/fi';
import { toast } from 'react-toastify';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';

const MockInterviewSession = () => {
  const { applicationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get application and questions from location state
  const application = location.state?.application;
  const questions = location.state?.questions || [];
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60); // 60 seconds = 1 minute
  const [showAnswer, setShowAnswer] = useState(false);
  const [completed, setCompleted] = useState([]);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const audioUrlRef = useRef(null);
  
  // Text-to-speech functionality
  const synth = window.speechSynthesis;
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  useEffect(() => {
    audioUrlRef.current = audioUrl;
  }, [audioUrl]);

  useEffect(() => {
    // If no application or questions, redirect back to mock interview page
    if (!application || questions.length === 0) {
      toast.error('Interview data not found');
      navigate('/jobs/mock-interview');
    }
    
    // Initialize completed questions array
    setCompleted(new Array(questions.length).fill(false));
    
    return () => {
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (synth.speaking) {
        synth.cancel();
      }
    };
  }, []);
  
  // Handle recording start
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioBlob(audioBlob);
        setAudioUrl(audioUrl);
        
        // Mark this question as completed
        const newCompleted = [...completed];
        newCompleted[currentQuestionIndex] = true;
        setCompleted(newCompleted);
        
        // Stop all tracks from the stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      
      // Start the timer
      setTimeLeft(60);
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            stopRecording();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Could not access microphone. Please check your permissions.');
    }
  };
  
  // Handle recording stop
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Clear the timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };
  
  // Handle audio playback
  const togglePlayback = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    
    setIsPlaying(!isPlaying);
  };
  
  // Handle audio ended event
  const handleAudioEnded = () => {
    setIsPlaying(false);
  };
  
  // Navigate to next question
  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      // Clean up current audio
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setAudioBlob(null);
      setAudioUrl(null);
      setIsPlaying(false);
      setShowAnswer(false);
    } else {
      // End of interview
      toast.success('Mock interview completed!');
    }
  };
  
  // Navigate to previous question
  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      // Clean up current audio
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setAudioBlob(null);
      setAudioUrl(null);
      setIsPlaying(false);
      setShowAnswer(false);
    }
  };
  
  // Read question aloud using text-to-speech
  const readQuestion = () => {
    if (synth.speaking) {
      synth.cancel();
      setIsSpeaking(false);
      return;
    }
    
    const utterance = new SpeechSynthesisUtterance(questions[currentQuestionIndex].question);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    setIsSpeaking(true);
    synth.speak(utterance);
  };
  
  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  
  // If no application data, show loading
  if (!application) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  const currentQuestion = questions[currentQuestionIndex];
  
  const copy = pageDescriptions.mockInterviewSession;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <PageHeader
        title={copy.title}
        description={copy.description}
        whatYouCanDo={copy.whatYouCanDo}
        whatYouShouldDo={copy.whatYouShouldDo}
        actions={
          <button
            onClick={() => navigate('/jobs/mock-interview')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Exit Interview
          </button>
        }
      />
      
      <div className="card overflow-hidden mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            {application.company} - {application.jobTitle}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Question {currentQuestionIndex + 1} of {questions.length}
          </p>
        </div>
        
        <div className="border-t border-gray-200">
          {/* Progress bar */}
          <div className="w-full bg-gray-200 h-1">
            <div 
              className="bg-primary-600 h-1" 
              style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
            ></div>
          </div>
          
          {/* Question */}
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Question {currentQuestionIndex + 1}</h3>
              <button
                onClick={readQuestion}
                className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {isSpeaking ? 'Stop Reading' : 'Read Aloud'}
              </button>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-md mb-6">
              <p className="text-gray-900">{currentQuestion.question}</p>
            </div>
            
            {/* Recording controls */}
            <div className="flex flex-col items-center justify-center mb-6">
              {isRecording ? (
                <div className="flex flex-col items-center">
                  <div className="flex items-center justify-center mb-4">
                    <div className="animate-pulse bg-red-500 h-4 w-4 rounded-full mr-2"></div>
                    <span className="text-red-500 font-medium">Recording</span>
                  </div>
                  
                  <div className="text-2xl font-bold mb-4">
                    {formatTime(timeLeft)}
                  </div>
                  
                  <button
                    onClick={stopRecording}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <FiMicOff className="h-5 w-5 mr-2" />
                    Stop Recording
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  {audioUrl ? (
                    <div className="w-full max-w-md">
                      <audio 
                        ref={audioRef} 
                        src={audioUrl} 
                        onEnded={handleAudioEnded}
                        className="w-full mb-4" 
                        controls
                      />
                      
                      <div className="flex justify-center">
                        <button
                          onClick={() => {
                            setAudioBlob(null);
                            setAudioUrl(null);
                          }}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mr-4"
                        >
                          Record Again
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={startRecording}
                      className="inline-flex items-center px-6 py-3 border border-transparent rounded-full shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <FiMic className="h-5 w-5 mr-2" />
                      Start Recording (1 minute max)
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {/* Suggested answer */}
            <div className="mt-8">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-md font-medium text-gray-900">Suggested Answer</h4>
                <button
                  onClick={() => setShowAnswer(!showAnswer)}
                  className="inline-flex items-center text-sm text-primary-600 hover:text-primary-800"
                >
                  {showAnswer ? (
                    <>
                      <FiEyeOff className="h-4 w-4 mr-1" />
                      Hide Answer
                    </>
                  ) : (
                    <>
                      <FiEye className="h-4 w-4 mr-1" />
                      Show Answer
                    </>
                  )}
                </button>
              </div>
              
              {showAnswer && (
                <div className="bg-primary-50 p-4 rounded-md">
                  {/* Display the suggested answer */}
                  <p className="text-gray-800">{currentQuestion.suggested_answer}</p>
                  
                  {/* Display tips if available */}
                  {currentQuestion.tips && (
                    <div className="mt-4 pt-4 border-t border-primary-100">
                      <h5 className="text-sm font-medium text-primary-800 mb-2">Tips:</h5>
                      <p className="text-sm text-primary-700">{currentQuestion.tips}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Navigation buttons */}
      <div className="flex justify-between">
        <button
          onClick={prevQuestion}
          disabled={currentQuestionIndex === 0}
          className={`inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md ${
            currentQuestionIndex === 0
              ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
              : 'text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
          }`}
        >
          <FiArrowLeft className="mr-2 h-5 w-5" />
          Previous Question
        </button>
        
        <button
          onClick={nextQuestion}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          {currentQuestionIndex < questions.length - 1 ? (
            <>
              Next Question
              <FiArrowRight className="ml-2 h-5 w-5" />
            </>
          ) : (
            <>
              Finish Interview
              <FiCheck className="ml-2 h-5 w-5" />
            </>
          )}
        </button>
      </div>
      
      {/* Question navigation dots */}
      <div className="flex justify-center mt-6">
        {questions.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              // Clean up current audio
              if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
              }
              
              setCurrentQuestionIndex(index);
              setAudioBlob(null);
              setAudioUrl(null);
              setIsPlaying(false);
              setShowAnswer(false);
            }}
            className={`h-3 w-3 mx-1 rounded-full ${
              index === currentQuestionIndex
                ? 'bg-primary-600'
                : completed[index]
                ? 'bg-green-500'
                : 'bg-gray-300'
            }`}
            aria-label={`Go to question ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default MockInterviewSession;